/**
 * Pure geometry/analytics for the Coach's Tactics Board.
 *
 * Inputs are player and ball positions in CENTIMETRES from the centre spot,
 * y up-positive — the frame the tracking is already in. These functions make
 * no DOM/SVG assumptions; the board maps the results to the pitch.
 *
 * Direction-agnostic by default. Where a notion of "attacking +x" is needed
 * (formation, roles), the coach's orientation toggle supplies it — the
 * tracking alone does not say which way a team attacks.
 */

export type TPlayer = { team: number; jersey: number; x: number; y: number }
export type TPoint = { x: number; y: number }
export type TFrame = {
  frame: number
  players: TPlayer[]
  ball: { x: number; y: number } | null
}

export const PITCH_L_CM = 10500
export const PITCH_W_CM = 6800
const HALF_L = PITCH_L_CM / 2
const HALF_W = PITCH_W_CM / 2
const BOX_DEPTH = 1650 // 16.5 m penalty area depth
const BOX_HALF_W = 2016 // 40.32 m / 2 penalty area half-width
// The one radius within which a player is deemed to have the ball — used by
// possession, pressure, lanes and commentary alike.
export const OWNER_RADIUS_CM = 250

const onlyTeams = (ps: TPlayer[]) => ps.filter((p) => p.team === 0 || p.team === 1)

// ── Pitch control: a coarse Voronoi (nearest player wins the cell) ──────────
export type ControlCell = { x0: number; y0: number; w: number; h: number; team: number }

/** Each grid cell coloured by the team of the nearest player — a cheap read on
 *  who controls which space. Coordinates in cm, cell origin at its low corner. */
export function controlGrid(players: TPlayer[], cols = 24, rows = 15): ControlCell[] {
  const outs = onlyTeams(players)
  if (outs.length === 0) return []
  const cw = PITCH_L_CM / cols, ch = PITCH_W_CM / rows
  const cells: ControlCell[] = []
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const cx = -HALF_L + (c + 0.5) * cw
      const cy = -HALF_W + (r + 0.5) * ch
      let best = outs[0]!, bd = Infinity
      for (const p of outs) {
        const d = (p.x - cx) ** 2 + (p.y - cy) ** 2
        if (d < bd) { bd = d; best = p }
      }
      cells.push({ x0: -HALF_L + c * cw, y0: -HALF_W + r * ch, w: cw, h: ch, team: best.team })
    }
  }
  return cells
}

// ── Team block: convex hull + its width and depth ───────────────────────────
/** Convex hull (Andrew's monotone chain), counter-clockwise, no repeated end. */
export function convexHull(pts: TPoint[]): TPoint[] {
  const ps = [...pts].sort((a, b) => a.x - b.x || a.y - b.y)
  if (ps.length <= 2) return ps
  const cross = (o: TPoint, a: TPoint, b: TPoint) =>
    (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x)
  const lower: TPoint[] = []
  for (const p of ps) {
    while (lower.length >= 2 && cross(lower[lower.length - 2]!, lower[lower.length - 1]!, p) <= 0) lower.pop()
    lower.push(p)
  }
  const upper: TPoint[] = []
  for (let i = ps.length - 1; i >= 0; i--) {
    const p = ps[i]!
    while (upper.length >= 2 && cross(upper[upper.length - 2]!, upper[upper.length - 1]!, p) <= 0) upper.pop()
    upper.push(p)
  }
  lower.pop(); upper.pop()
  return lower.concat(upper)
}

/** A team's shape as the convex hull of its players (the only thing the board
 *  draws for the Block overlay). */
export function teamBlock(players: TPlayer[], team: number): TPoint[] {
  const pts = players.filter((p) => p.team === team).map((p) => ({ x: p.x, y: p.y }))
  return pts.length < 3 ? pts : convexHull(pts)
}

// ── Pressure on the carrier: nearest opponent distance (cm, -1 if none) ──────
export function pressureCm(carrier: TPoint, opponents: TPoint[]): number {
  let best = Infinity
  for (const o of opponents) best = Math.min(best, Math.hypot(o.x - carrier.x, o.y - carrier.y))
  return best === Infinity ? -1 : best
}

// ── Open passing lanes: teammates with a clear corridor from the carrier ─────
export function openLanes(
  carrier: TPlayer, team: TPlayer[], opp: TPlayer[], corridorCm = 250): TPlayer[] {
  const open: TPlayer[] = []
  for (const t of team) {
    if (t.jersey === carrier.jersey && t.team === carrier.team) continue
    const dx = t.x - carrier.x, dy = t.y - carrier.y
    const len2 = dx * dx + dy * dy
    if (len2 === 0) continue
    let blocked = false
    for (const o of opp) {
      const s = ((o.x - carrier.x) * dx + (o.y - carrier.y) * dy) / len2
      if (s <= 0.05 || s >= 0.95) continue // only opponents between the two
      const px = carrier.x + s * dx, py = carrier.y + s * dy
      if (Math.hypot(o.x - px, o.y - py) < corridorCm) { blocked = true; break }
    }
    if (!blocked) open.push(t)
  }
  return open
}

/** Who is nearest the ball within `radiusCm`, as a team_jersey key. */
export function ballOwnerKey(f: TFrame, radiusCm = OWNER_RADIUS_CM): string | null {
  if (!f.ball) return null
  let best: string | null = null, bd = radiusCm
  for (const p of f.players) {
    const d = Math.hypot(p.x - f.ball.x, p.y - f.ball.y)
    if (d < bd) { bd = d; best = `${p.team}_${p.jersey}` }
  }
  return best
}

/** Settled possessions: maximal runs of >= `minHold` consecutive frames with
 *  the same owner. The one implementation shared by the pass count and the
 *  possession-change card detection. */
export function settledOwners(
  frames: TFrame[], minHold: number, radiusCm = OWNER_RADIUS_CM): { idx: number; owner: string }[] {
  const owners = frames.map((f) => ballOwnerKey(f, radiusCm))
  const out: { idx: number; owner: string }[] = []
  for (let i = 0; i < owners.length;) {
    const o = owners[i] ?? null
    let j = i
    while (j < owners.length && owners[j] === o) j++
    if (o !== null && j - i >= minHold) out.push({ idx: i, owner: o })
    i = j
  }
  return out
}

// ── Sequence metrics: what the whole clip achieved, from tracking alone ──────
export type SequenceMetrics = {
  durationS: number
  passes: number
  ballMoveM: number
  topSpeedKmh: number
  enteredBox: boolean
}

// A stationary ball jitters a few cm per frame in tracking data; ignore steps
// at or below this so "ball travelled" is not inflated by noise. Real touches
// move the ball well beyond it.
const BALL_STEP_FLOOR_CM = 10

export function sequenceMetrics(
  frames: TFrame[], fps: number, minHoldS = 0.3): SequenceMetrics {
  if (frames.length === 0) return { durationS: 0, passes: 0, ballMoveM: 0, topSpeedKmh: 0, enteredBox: false }
  const first = frames[0]!, last = frames[frames.length - 1]!
  // Passes = changes of SETTLED possession, on the SAME settle threshold the
  // deck uses, so the panel and the cards agree.
  const minHold = Math.max(3, Math.round(minHoldS * fps))
  const settled = settledOwners(frames, minHold)
  let passes = 0
  for (let k = 1; k < settled.length; k++) {
    const a = settled[k - 1]!.owner, b = settled[k]!.owner
    // A pass keeps the ball in the same team with a new player; a turnover
    // (change of team) is not a pass.
    if (b !== a && b.split('_')[0] === a.split('_')[0]) passes++
  }
  let enteredBox = false, topSpeed = 0, ballMoveM = 0
  let prev = new Map<string, TPlayer>()
  let prevFrame = first.frame
  let prevBall = first.ball
  for (let i = 0; i < frames.length; i++) {
    const f = frames[i]!
    if (f.ball && Math.abs(f.ball.x) > HALF_L - BOX_DEPTH && Math.abs(f.ball.y) < BOX_HALF_W) enteredBox = true
    const dFrames = f.frame - prevFrame
    if (i > 0 && dFrames > 0) {
      const dt = dFrames / fps // real elapsed seconds, so a gap does not inflate speed
      for (const p of f.players) {
        const q = prev.get(`${p.team}_${p.jersey}`)
        if (!q) continue
        const kmh = (Math.hypot(p.x - q.x, p.y - q.y) / 100 / dt) * 3.6
        if (kmh > topSpeed && kmh < 45) topSpeed = kmh // 45 km/h guards track-swap outliers
      }
      // ballMove is distance TRAVELLED (summed path), not net displacement, so
      // a sequence that circulates and returns does not read ~0 m. A per-step
      // dead-band drops sub-floor jitter — a stationary ball wobbles a few cm
      // per frame, which else sums to tens of metres over a long clip — the
      // same kind of guard as the 45 km/h speed ceiling above.
      if (f.ball && prevBall) {
        const stepCm = Math.hypot(f.ball.x - prevBall.x, f.ball.y - prevBall.y)
        if (stepCm > BALL_STEP_FLOOR_CM) ballMoveM += stepCm / 100
      }
    }
    prev = new Map(f.players.map((p) => [`${p.team}_${p.jersey}`, p]))
    prevFrame = f.frame
    if (f.ball) prevBall = f.ball
  }
  return { durationS: (last.frame - first.frame) / fps, passes, ballMoveM, topSpeedKmh: topSpeed, enteredBox }
}
